import React, { useState } from 'react';
import { ArrowLeft, CheckCircle, Camera, AlertTriangle } from 'lucide-react';
interface SecurityChecklistProps {
  onComplete: (hasIssue: boolean) => void;
}
export const SecurityChecklist: React.FC<SecurityChecklistProps> = ({
  onComplete
}) => {
  const [completedChecks, setCompletedChecks] = useState<string[]>([]);
  const [hasIssue, setHasIssue] = useState(false);
  const [activeCheck, setActiveCheck] = useState<string | null>(null);
  const securityChecks = [{
    id: 'doors',
    name: 'Exterior Doors Locked',
    requiresPhoto: true
  }, {
    id: 'windows',
    name: 'Windows Secured',
    requiresPhoto: false
  }, {
    id: 'alarm',
    name: 'Alarm System Armed',
    requiresPhoto: true
  }, {
    id: 'lights',
    name: 'Exterior Lights On',
    requiresPhoto: false
  }, {
    id: 'cameras',
    name: 'Security Cameras Operational',
    requiresPhoto: false
  }, {
    id: 'safe',
    name: 'Safe Locked',
    requiresPhoto: true
  }];
  const toggleCheck = (checkId: string) => {
    if (completedChecks.includes(checkId)) {
      setCompletedChecks(prev => prev.filter(id => id !== checkId));
    } else {
      if (securityChecks.find(check => check.id === checkId)?.requiresPhoto) {
        setActiveCheck(checkId);
      } else {
        setCompletedChecks(prev => [...prev, checkId]);
      }
    }
  };
  const completePhotoCheck = () => {
    if (activeCheck) {
      setCompletedChecks(prev => [...prev, activeCheck]);
      setActiveCheck(null);
    }
  };
  const reportIssue = () => {
    setHasIssue(true);
    setActiveCheck(null);
  };
  const allChecksCompleted = securityChecks.every(check => completedChecks.includes(check.id));
  return <div className="space-y-4" data-id="element-148">
      <div className="flex items-center mb-4" data-id="element-149">
        <button onClick={() => onComplete(false)} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 mr-2" data-id="element-150">
          <ArrowLeft className="w-5 h-5 text-gray-700" data-id="element-151" />
        </button>
        <h2 className="text-lg font-semibold text-gray-900" data-id="element-152">
          Security Checklist
        </h2>
      </div>
      {activeCheck ? <div className="bg-white rounded-lg shadow p-4" data-id="element-153">
          <h3 className="font-medium text-gray-900 mb-4" data-id="element-154">
            {securityChecks.find(check => check.id === activeCheck)?.name}
          </h3>
          <div className="space-y-4" data-id="element-155">
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center" data-id="element-156">
              <button className="px-4 py-2 bg-blue-100 text-blue-700 rounded-md text-sm font-medium inline-flex items-center" data-id="element-157">
                <Camera className="w-4 h-4 mr-2" data-id="element-158" />
                Take Photo
              </button>
              <p className="text-xs text-gray-500 mt-2" data-id="element-159">
                Take a clear photo as verification
              </p>
            </div>
            <div className="flex space-x-3" data-id="element-160">
              <button onClick={completePhotoCheck} className="flex-1 bg-blue-600 text-white py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors" data-id="element-161">
                Confirm
              </button>
              <button onClick={reportIssue} className="flex-1 bg-white border border-gray-300 text-gray-700 py-2 rounded-lg font-medium hover:bg-gray-50 transition-colors" data-id="element-162">
                Report Issue
              </button>
            </div>
          </div>
        </div> : <>
          <div className="bg-white rounded-lg shadow p-4" data-id="element-163">
            <h3 className="font-medium text-gray-900 mb-4" data-id="element-164">
              Complete Security Checks
            </h3>
            {hasIssue && <div className="mb-4 p-3 bg-yellow-50 rounded-md flex" data-id="element-165">
                <AlertTriangle className="w-5 h-5 text-yellow-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-166" />
                <div data-id="element-167">
                  <p className="text-sm font-medium text-gray-900" data-id="element-168">
                    Security issue reported
                  </p>
                  <p className="text-xs text-gray-500 mt-1" data-id="element-169">
                    Manager review will be required
                  </p>
                </div>
              </div>}
            <div className="space-y-2" data-id="element-170">
              {securityChecks.map(check => <div key={check.id} className={`flex items-center justify-between p-3 rounded-md cursor-pointer ${completedChecks.includes(check.id) ? 'bg-green-50' : 'bg-white border border-gray-200'}`} onClick={() => toggleCheck(check.id)} data-id="element-171">
                  <div className="flex items-center" data-id="element-172">
                    {completedChecks.includes(check.id) ? <CheckCircle className="w-5 h-5 text-green-500 mr-3" data-id="element-173" /> : <div className="w-5 h-5 rounded-full border-2 border-gray-300 mr-3" data-id="element-174" />}
                    <div data-id="element-175">
                      <p className="text-sm font-medium text-gray-900" data-id="element-176">
                        {check.name}
                      </p>
                      {check.requiresPhoto && <p className="text-xs text-blue-600" data-id="element-177">Photo required</p>}
                    </div>
                  </div>
                </div>)}
            </div>
          </div>
          {allChecksCompleted && <button onClick={() => onComplete(hasIssue)} className="w-full bg-blue-600 text-white py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors mt-4" data-id="element-178">
              Complete Security Checklist
            </button>}
        </>}
    </div>;
};